import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { HvDatePicker } from "@hitachivantara/uikit-react-core";
import {
  HvTimePicker,
  HvTimePickerValue,
} from "@hitachivantara/uikit-react-lab";
import {
  getDateTimeInUTCByZoneWithoutFormat,
  getFormattedDateTimeWithTZ,
  standardDateFormat,
} from "lib/utils/datetime";

import { AttributeFormData, useTimeSettings } from "lib/api";
import { useStyles } from "./styles";

interface DateTimeProps {
  attribute: AttributeFormData;
  onChange: (value: string) => void;
}

const parseTime = (value: string) => {
  return value
    ? {
        hours: moment(value).get("hours"),
        minutes: moment(value).get("minutes"),
        seconds: moment(value).get("seconds"),
      }
    : undefined;
};

const parseDate = (value: string) => {
  return value ? new Date(value) : undefined;
};

const DateTime: React.FC<DateTimeProps> = ({ attribute, onChange }) => {
  const { timezone } = useTimeSettings();
  const { value, label } = attribute;
  const timezoneFormattedDate = value
    ? getFormattedDateTimeWithTZ(value as string, timezone)
    : undefined;

  const { t } = useTranslation("cameraAddEdit");
  const classes = useStyles();
  const [dateValue, setDateValue] = useState<Date | undefined>(
    parseDate(timezoneFormattedDate as string)
  );
  const [timeValue, setTimeValue] = useState<HvTimePickerValue | undefined>(
    parseTime(timezoneFormattedDate as string)
  );

  const parse = (
    date: Date | undefined,
    time: HvTimePickerValue | undefined
  ) => {
    if (date) {
      const newDate = time
        ? moment(date).set(time).toISOString()
        : moment(date).toISOString();
      const finalFormatterDate = moment(
        getDateTimeInUTCByZoneWithoutFormat(newDate, timezone)
      ).format(standardDateFormat);
      onChange(finalFormatterDate);
    }
  };

  return (
    <>
      <HvDatePicker
        label={label as string}
        className={classes.datePicker}
        placeholder={t("properties.dateTime.datePlaceholder")}
        onChange={(date) => {
          parse(date, timeValue);
          setDateValue(date);
        }}
        value={dateValue}
      />
      <HvTimePicker
        className={classes.timePicker}
        placeholder={t("properties.dateTime.timePlaceholder")}
        onChange={(time) => {
          parse(dateValue, time);
          setTimeValue(time);
        }}
        timeFormat="24"
        value={timeValue}
        disableDefaultValue
      />
    </>
  );
};

export default DateTime;
